import {
  makeStyles,
  shorthands,
  Tab,
  TabList,
  tokens,
  type SelectTabData,
  type SelectTabEvent
} from "@fluentui/react-components";
import { useContext, useEffect, useState } from "react";
import { logEvent, logPageView } from "../../../../helper/AppInsightsHelper";
import { ProductViewFiltersContext } from "../../../../pages/ProductView";
import { useGetPartnerInfluenceDetailsMutation } from "../../../../slices/ExploreProducts.slice";
import ConsumptionTab from "./ConsumptionTab";
import EntraIDPartnerInfluenceTab from "./EntraIDPartnerInfluenceTab";
import { EntraEliteFilter, PartnerInfluenceData, InnerTab } from "./partnerInfluence.interfaces";
import DisplayAPIsException from "../../../../BasicComponents/DisplayAPIsExceptionAlert";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap(tokens.spacingVerticalS),
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalM)
  },
  tabList: {
    ...shorthands.borderBottom("1px", "solid", tokens.colorNeutralStroke2)
  }
});

const noDataStyle = {
  alignItems: "center",
  display: "flex",
  fontSize: 18,
  fontWeight: 600,
  justifyContent: "center",
  padding: 200
} as const;

const hasPartnerData = (data: PartnerInfluenceData | undefined): boolean => {
  if (!data) return false;
  return (
    (data.usageLicensesDetails?.length ?? 0) > 0 ||
    (data.partnerTenantsDetails?.length ?? 0) > 0 ||
    (data.partnerInfluenceDetails?.length ?? 0) > 0 ||
    (data.entraTrendDetails?.length ?? 0) > 0 ||
    (data.entraPartnerInfluenceDetails?.length ?? 0) > 0 ||
    (data.entraIGTrendDetails?.length ?? 0) > 0 ||
    (data.entraIGPartnerInfluenceDetails?.length ?? 0) > 0
  );
};

const PartnerInfluence = () => {
  const styles = useStyles();
  const [selectedTab, setSelectedTab] = useState<InnerTab>("consumption");
  const filters = useContext(ProductViewFiltersContext);
  const productName = filters.productName ?? "";
  const isSentinel = productName.toLowerCase() === "microsoft sentinel";
  const isEntraID = productName.toLowerCase() === "microsoft entra id";
  const isEntraIG = productName.toLowerCase() === "microsoft entra identity governance";
  const usesDedicatedPartnerData = isSentinel || isEntraID || isEntraIG;
  const [entraEliteFilter, setEntraEliteFilter] = useState<EntraEliteFilter>("All");
  const [partnerInfluenceData, setPartnerInfluenceData] = useState<PartnerInfluenceData | undefined>(undefined);
  const [getPartnerInfluenceDetails, { error, isLoading }] = useGetPartnerInfluenceDetailsMutation();

  const onTabSelect = (_event: SelectTabEvent, tabData: SelectTabData) => {
    setSelectedTab(tabData.value as InnerTab);
  };

  const handleAPIRequest = async () => {
    const filterValue = (value: string | undefined) => usesDedicatedPartnerData ? "" : value;
    const request = {
      selectedTabName: selectedTab,
      hasPartner: filterValue(filters.hasPartner),
      industry: filterValue(filters.industry),
      isCAT: filterValue(filters.isCAT),
      isFastTrackStrategic: filterValue(filters.isFastTrackStrategic),
      isSST: filterValue(filters.isSST),
      ProductName: productName,
      Region: filterValue(filters.region),
      segmentGroup: filterValue(filters.segmentGroup),
      segment: filterValue(filters.segment),
      subsegment: filterValue(filters.subSegment),
      Vertical: "",
      isS500: filterValue(filters.isS500),
      isCCP: filterValue(filters.isCCP),
      isTCT: filterValue(filters.isTCT),
      majorGrowth: filterValue(filters.majorGrowth),
      isElite: isEntraID ? entraEliteFilter : ""
    };

    try {
      const response = await getPartnerInfluenceDetails(request).unwrap();
      setPartnerInfluenceData(response);
    } catch (requestException) {
      setPartnerInfluenceData(undefined);
      logEvent("Partner Influence - Consumption Tab", "Product View Page", "Partner Influence", {
        error: { "API Failure": requestException }
      });
    }
  };

  const displayAPIException = error && <DisplayAPIsException error={error} height={250} />;

  useEffect(() => {
    logPageView("Partner Influence - Consumption Tab", "Product View Page", "Partner Influence");
  }, [])

  useEffect(() => {
    handleAPIRequest();
    logEvent("Partner Influence - Consumption Tab", "Product View Page", "Partner Influence", "Filters or Tab Changed");
  }, [
    selectedTab,
    filters.hasPartner,
    filters.industry,
    filters.isCAT,
    filters.isFastTrackStrategic,
    filters.isSST,
    filters.productName,
    filters.region,
    filters.segmentGroup,
    filters.segment,
    filters.subSegment,
    filters.isS500,
    filters.isCCP,
    filters.isTCT,
    filters.majorGrowth,
    entraEliteFilter
  ]);

  return (
    <div className={styles.root}>
      <TabList
        className={styles.tabList}
        selectedValue={selectedTab}
        onTabSelect={onTabSelect}
        aria-label="Partner Influence Tabs"
      >
        <Tab value="consumption">{usesDedicatedPartnerData ? "Licensed" : "Consumption"}</Tab>
      </TabList>
      {selectedTab === "consumption" && (isEntraID || isEntraIG) && (isLoading || hasPartnerData(partnerInfluenceData)) && (
        <EntraIDPartnerInfluenceTab
          data={partnerInfluenceData}
          eliteFilter={entraEliteFilter}
          isEntraIG={isEntraIG}
          isLoading={isLoading}
          onEliteFilterChange={setEntraEliteFilter}
        />
      )}
      {selectedTab === "consumption" && !isEntraID && !isEntraIG && isLoading && (
        <ConsumptionTab data={partnerInfluenceData} isLoading={isLoading} productName={productName} />
      )}
      {selectedTab === "consumption" && !isEntraID && !isEntraIG && !isLoading && hasPartnerData(partnerInfluenceData) && (
        <ConsumptionTab data={partnerInfluenceData} isLoading={isLoading} productName={productName} />
      )}
      {selectedTab === "consumption" && !isLoading && !error && partnerInfluenceData && !hasPartnerData(partnerInfluenceData) && (
        <div style={noDataStyle}>{"Partner Influence Data is not available for " + filters.productName + "."}</div>
      )}
      {/* handle API exceptions */}
      {partnerInfluenceData === undefined && (error) && displayAPIException}
    </div>
  );
};

export default PartnerInfluence;
